import store from '@/store';
import { computed, ref, watch } from '@vue/composition-api';
import { mdiFileExcelOutline, mdiFileImageOutline, mdiFileOutline } from '@mdi/js';
import Vue from 'vue';

export default function useChargebackFilesList(fileType) {
  const isLoading = ref(false);
  const options = ref({
    page: 1,
    itemsPerPage: 24,
  });
  const files = computed(() => store.state.chargebackFileV2.files);
  const totalCount = computed(() => store.state.chargebackFileV2.totalCount);

  function fetchFiles() {
    isLoading.value = true;

    store
      .dispatch('chargebackFileV2/fetchFiles', { fileType, page: options.value.page, pageSize: options.value.itemsPerPage })
      .catch(() => {
        Vue.notify({
          type: 'error',
          title: 'Verifi Files',
          text: 'Failed to load files list',
        });
      })
      .finally(() => isLoading.value = false);
  }

  const deleteFile = (id, fileName) => {
    Vue.$confirm({
      message: `Are you sure you want to delete ${fileName}?`,
      button: {
        no: 'No',
        yes: 'Yes',
      },
      callback: (confirm) => {
        if (!confirm) {
          return;
        }

        store
          .dispatch('chargebackFileV2/deleteFile', id)
          .catch(() => {
            Vue.notify({
              type: 'error',
              title: 'Verifi Files',
              text: `Failed to remove file ${fileName}`,
            });
          });
      },
    });
  };

  const parseFile = (id, fileName) => {
    Vue.$confirm({
      message: `Are you sure you want to parse ${fileName}?`,
      button: {
        no: 'No',
        yes: 'Yes',
      },
      callback: (confirm) => {
        if (!confirm) {
          return;
        }

        store
          .dispatch('chargebackFileV2/parseFile', id)
          .then(() => {
            Vue.notify({
              type: 'success',
              title: 'RDR Files',
              text: 'Parsing has been triggered. Progress and results will appear in the #merchanto-server-notice Slack channel.',
            });
          })
          .catch(error => {
            Vue.notify({
              type: 'error',
              title: 'RDR Files',
              text: error?.response?.data?.message || `Failed to trigger parsing for ${fileName}`,
            });
          });
      },
    });
  };

  const downloadFile = (id, fileName) => {
    store
      .dispatch('chargebackFileV2/getDownloadUrl', id)
      .then(fileUrl => window.open(fileUrl, '_blank'))
      .catch(() => {
        Vue.notify({
          type: 'error',
          title: 'Verifi Files',
          text: `Failed to download file ${fileName}`,
        });
      });
  };

  const resolveFileTypeVariant = (fileMimeType) => {
    const fileTypeMapping = {
      'image/jpg': mdiFileImageOutline,
      'image/png': mdiFileImageOutline,
      'image/jpeg': mdiFileImageOutline,
      'text/xml': mdiFileExcelOutline,
      'text/csv': mdiFileExcelOutline,
    };

    return fileTypeMapping[fileMimeType] || mdiFileOutline;
  };

  if (fileType) {
    watch(options, fetchFiles, { deep: true });
    fetchFiles();
  }

  return {
    isLoading,
    fetchFiles,
    deleteFile,
    parseFile,
    downloadFile,
    resolveFileTypeVariant,
    files,
    totalCount,
    options,
    fileType,
  };
}
